import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Reveal, Label } from "@/components/Reveal";
import RootsFlow from "@/components/RootsFlow";
import TopoBackground from "@/components/TopoBackground";
import Parallax from "@/components/Parallax";
import { MEDIA } from "@/data/content";

const AI_INTERACTIONS = [
  "Tell me about this place.",
  "What flowers here after the first rains?",
  "What language names exist for this species?",
  "What restoration work has happened here?",
  "What stories belong to this place?",
];

const AI_TRAITS = ["Processes information.", "Finds patterns.", "Answers questions.", "Accelerates analysis.", "Supports decisions."];
const SI_TRAITS = ["Strengthens relationships.", "Learns from Country.", "Honours community authority.", "Thinks across generations.", "Transforms knowledge into care."];

const EMERGES = [
  "A place name", "A story", "Language, heard", "Bird calls identified",
  "A seasonal calendar", "Traditional burning history", "Native seed collection notes",
  "Historical photographs", "Elder audio", "Restoration projects",
];

const RIVER_IMG = "https://images.unsplash.com/photo-1712846040326-a3d06c0b1667?crop=entropy&cs=srgb&fm=jpg&q=85&w=1600";

export default function StewardshipIntelligence() {
  const [showButton, setShowButton] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setShowButton(true), 2600);
    return () => clearTimeout(t);
  }, []);

  return (
    <div data-testid="stewardship-intelligence-page">
      {/* HERO */}
      <section className="relative h-screen min-h-[640px] w-full overflow-hidden">
        <Parallax strength={80}>
          <video
            className="h-full w-full object-cover"
            autoPlay muted loop playsInline poster={MEDIA.heroPoster}
          >
            <source src={MEDIA.heroVideo} type="video/mp4" />
            <source src={MEDIA.heroVideoAlt} type="video/mp4" />
          </video>
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-b from-[#050806]/55 via-[#050806]/30 to-[#050806]" />
        <div className="relative z-10 h-full max-w-6xl mx-auto px-6 md:px-12 flex flex-col justify-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="max-w-3xl"
          >
            <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#F4F1ED]">
              Stewardship Intelligence
            </h1>
            <p className="mt-8 max-w-2xl text-xl md:text-2xl text-[#E4E0D9] leading-relaxed font-light">
              The intelligence that enables people and communities to care well for what has
              been entrusted to them.
            </p>
            <p className="mt-8 max-w-xl text-base text-[#A8A5A1] leading-relaxed">
              Artificial Intelligence helps us process information. Stewardship Intelligence
              helps us care for people, place and future generations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* PHILOSOPHY — split */}
      <section className="border-b border-white/5">
        <div className="grid lg:grid-cols-2">
          <div className="relative min-h-[420px] lg:min-h-[720px] overflow-hidden">
            <img src={MEDIA.placeWide} alt="A living landscape at golden light" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050806]/60 to-transparent" />
          </div>
          <div className="flex items-center px-6 md:px-12 lg:px-20 py-24 lg:py-0">
            <div className="max-w-xl">
              <Reveal>
                <Label className="block mb-6">Philosophy</Label>
                <h2 className="font-display font-light text-4xl sm:text-5xl text-[#F4F1ED] leading-[1.08]">
                  Wisdom Sits in Places
                </h2>
              </Reveal>
              <Reveal delay={0.12}>
                <div className="mt-8 space-y-5 text-lg text-[#A8A5A1] leading-relaxed">
                  <p>
                    For thousands of generations, wisdom has not been stored in books or
                    databases. It has lived within Country. Within stories. Within language.
                    Within seasons. Within relationships.
                  </p>
                  <p>People become wise by learning from places.</p>
                  <p>
                    Technology should never replace this relationship. Its purpose is to help
                    communities remember, reconnect and steward what already exists.
                  </p>
                </div>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* QUIET TRANSITION */}
      <section className="py-40 md:py-56">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="font-display font-light text-3xl sm:text-4xl lg:text-5xl leading-[1.3] text-[#F4F1ED]">
              Wisdom is not accumulated.{" "}
              <span className="text-[#D4A373]">Wisdom is cultivated.</span>
            </p>
          </Reveal>
        </div>
      </section>

      {/* RELATIONSHIP — roots/rivers flow */}
      <section className="relative py-24 md:py-32 border-y border-white/5 overflow-hidden">
        <TopoBackground opacity={0.05} />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12">
          <Reveal>
            <p className="text-center wk-label mb-16">Everything begins with Place</p>
          </Reveal>
          <Reveal delay={0.1}>
            <RootsFlow />
          </Reveal>
        </div>
      </section>

      {/* AI ON COUNTRY */}
      <section className="relative min-h-screen flex items-center overflow-hidden border-b border-white/5">
        <Parallax strength={60}>
          <img src={RIVER_IMG} alt="A person standing quietly beside a river" className="h-full w-full object-cover" />
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-r from-[#050806] via-[#050806]/70 to-[#050806]/20" />
        <div className="relative z-10 max-w-6xl mx-auto px-6 md:px-12 py-28 w-full">
          <div className="max-w-xl">
            <Reveal>
              <Label className="block mb-6">AI on Country</Label>
              <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] leading-tight">
                Every answer begins with place — never with information.
              </h2>
            </Reveal>
            <div className="mt-10 space-y-3">
              {AI_INTERACTIONS.map((q, i) => (
                <Reveal key={q} delay={0.15 + i * 0.12}>
                  <p className="font-display text-xl md:text-2xl text-[#E4E0D9] border-l border-[#D4A373]/40 pl-5 py-1">
                    “{q}”
                  </p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* COMPARISON */}
      <section className="py-28 md:py-40 border-b border-white/5">
        <div className="max-w-6xl mx-auto px-6 md:px-12 grid md:grid-cols-2 gap-16 lg:gap-32">
          <Reveal>
            <div>
              <h3 className="font-display font-light text-3xl text-[#A8A5A1]">Artificial Intelligence</h3>
              <div className="mt-8 space-y-4">
                {AI_TRAITS.map((t) => (
                  <p key={t} className="text-lg text-[#A8A5A1] border-b border-white/8 pb-4">{t}</p>
                ))}
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div>
              <h3 className="font-display font-light text-3xl text-[#F4F1ED]">Stewardship Intelligence</h3>
              <div className="mt-8 space-y-4">
                {SI_TRAITS.map((t) => (
                  <p key={t} className="text-lg text-[#F4F1ED] border-b border-[#D4A373]/25 pb-4">{t}</p>
                ))}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* COMMUNITY AUTHORITY */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden border-b border-white/5">
        <Parallax strength={60}>
          <img src={MEDIA.community} alt="Traditional Owners walking Country" className="h-full w-full object-cover" />
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/60 to-[#050806]/30" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-28 text-center">
          <Reveal>
            <h2 className="font-display font-light text-4xl sm:text-5xl text-[#F4F1ED]">
              Communities remain the keepers.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl mx-auto text-lg text-[#D8D4CE] leading-relaxed">
              Artificial Intelligence never replaces cultural authority. Knowledge remains
              governed by communities. Permissions remain cultural. Country remains the
              teacher. Technology serves stewardship — never ownership.
            </p>
          </Reveal>
        </div>
      </section>

      {/* LIVING EXAMPLE */}
      <section className="py-28 md:py-40 border-b border-white/5">
        <div className="max-w-5xl mx-auto px-6 md:px-12">
          <Reveal>
            <Label className="block mb-4">A living example</Label>
            <h2 className="font-display font-light text-3xl sm:text-4xl lg:text-5xl text-[#F4F1ED] max-w-3xl">
              As you walk, knowledge emerges naturally from place.
            </h2>
          </Reveal>
          <div className="mt-16 max-w-2xl mx-auto">
            {EMERGES.map((e, i) => (
              <Reveal key={e} delay={i * 0.06}>
                <div className="flex items-baseline gap-6 py-6 border-b border-white/8">
                  <span className="font-display text-lg text-[#D4A373]/40 w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
                  <span className="font-display text-2xl md:text-3xl text-[#F4F1ED]">{e}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* STEWARDSHIP — massive typography */}
      <section className="py-40 md:py-56">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center space-y-6">
          {["Knowledge is recorded.", "Wisdom is remembered.", "Stewardship is practiced."].map((line, i) => (
            <Reveal key={line} delay={i * 0.18}>
              <p className={`font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-tight ${i === 2 ? "text-[#D4A373]" : "text-[#F4F1ED]"}`}>
                {line}
              </p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* FUTURE */}
      <section className="relative min-h-[85vh] flex items-center overflow-hidden border-y border-white/5">
        <Parallax strength={60}>
          <img src={MEDIA.closing} alt="Children walking with Elders at sunrise" className="h-full w-full object-cover" />
        </Parallax>
        <div className="absolute inset-0 bg-gradient-to-t from-[#050806] via-[#050806]/55 to-[#050806]/25" />
        <div className="relative z-10 max-w-4xl mx-auto px-6 md:px-12 py-28 text-center">
          <Reveal>
            <h2 className="font-display font-light text-4xl sm:text-5xl lg:text-6xl text-[#F4F1ED] leading-tight">
              The Future Learns From Place
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <p className="mt-8 max-w-2xl mx-auto text-lg text-[#D8D4CE] leading-relaxed">
              Every generation inherits knowledge. Every generation chooses what will endure.
              Stewardship Intelligence helps communities strengthen the relationship between
              people, Country and future generations. The future is not built through more
              information. It is built through wiser relationships.
            </p>
          </Reveal>
        </div>
      </section>

      {/* FINAL STATEMENT + delayed button */}
      <section className="py-40 md:py-56">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.2] text-[#F4F1ED]">
              Wisdom sits in places.
              <br />
              <span className="text-[#D4A373]">Stewardship keeps it alive.</span>
            </p>
          </Reveal>
          <div className={`mt-14 transition-opacity duration-[1600ms] ${showButton ? "opacity-100" : "opacity-0"}`}>
            <Link
              to="/partner"
              data-testid="si-become-keeper"
              className="inline-flex items-center justify-center px-9 py-4 rounded-md border border-[#D4A373]/60 text-[#F4F1ED] hover:bg-[#D4A373] hover:text-[#050806] transition-all duration-500"
            >
              Become a Wisdom Keeper
            </Link>
          </div>
        </div>
      </section>

      {/* FINAL QUESTION */}
      <section className="py-40 md:py-56 border-t border-white/5">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <p className="font-display font-light text-4xl sm:text-5xl lg:text-6xl leading-[1.2] text-[#F4F1ED]">
              What has been entrusted to your care?
            </p>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
